import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package, AlertTriangle, TrendingDown, BarChart3,
  ChevronRight, RefreshCw, BookOpen, Plus, Sparkles, Loader2, ClipboardCheck
} from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader';
import { useItems, useStockSummary, useMovements } from '../hooks/useInventory';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../utils/api';

// ── helpers ────────────────────────────────────────────────────────────────────
const fmtNum = (n: number) =>
  new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(n);

const ALERT_CATEGORIES = [
  { label: 'Helados base', emoji: '🍦', cat: 'helados-base' },
  { label: 'Frutas', emoji: '🍓', cat: 'frutas' },
  { label: 'Lácteos', emoji: '🥛', cat: 'lácteos' },
  { label: 'Empaques', emoji: '📦', cat: 'empaques' },
  { label: 'Comida rápida', emoji: '🍔', cat: 'comida-rapida' },
];

// ── sub-components ─────────────────────────────────────────────────────────────
function StatCard({
  icon, label, value, sub, color = 'blue',
}: {
  icon: React.ReactNode; label: string; value: string | number; sub?: string; color?: string;
}) {
  const colors: Record<string, string> = {
    blue: 'from-blue-500 to-blue-700',
    red: 'from-red-500 to-red-700',
    emerald: 'from-emerald-500 to-emerald-700',
    amber: 'from-amber-400 to-amber-600',
    violet: 'from-violet-500 to-violet-700',
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colors[color]} p-5 text-white shadow-lg`}>
      <div className="absolute -right-4 -top-4 opacity-20">{icon}</div>
      <div className="relative">
        <p className="text-xs font-semibold uppercase tracking-widest opacity-80">{label}</p>
        <p className="mt-1 text-3xl font-bold">{value}</p>
        {sub && <p className="mt-1 text-xs opacity-70">{sub}</p>}
      </div>
    </div>
  );
}

function StockBadge({ qty, minStock }: { qty: number; minStock?: number }) {
  const min = minStock ?? 0;
  if (qty <= 0) return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
      <AlertTriangle size={10} /> Agotado
    </span>
  );
  if (min > 0 && qty <= min) return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
      <TrendingDown size={10} /> Bajo
    </span>
  );
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
      OK
    </span>
  );
}

function RecentMovementRow({ mov }: { mov: any }) {
  const typeColors: Record<string, string> = {
    IN: 'bg-emerald-100 text-emerald-700',
    OUT: 'bg-red-100 text-red-700',
    ADJUST: 'bg-blue-100 text-blue-700',
    TRANSFER: 'bg-violet-100 text-violet-700',
  };
  const typeLabels: Record<string, string> = {
    IN: 'Entrada', OUT: 'Salida', ADJUST: 'Ajuste', TRANSFER: 'Traslado',
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-[color:var(--color-border)]/40 last:border-0">
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${typeColors[mov.type] ?? 'bg-muted text-muted'}`}>
          {typeLabels[mov.type] ?? mov.type}
        </span>
        <span className="text-sm text-[color:var(--color-text)] line-clamp-1">{mov.reason ?? 'Sin motivo'}</span>
      </div>
      <span className={`text-sm font-bold ${mov.type === 'OUT' ? 'text-red-600' : 'text-emerald-600'}`}>
        {mov.type === 'OUT' ? '-' : '+'}{fmtNum(mov.qty)}
      </span>
    </div>
  );
}

// ── main page ──────────────────────────────────────────────────────────────────
const InventoryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const { data: itemsPage, isLoading: loadingItems } = useItems({ limit: 100 });
  const { data: summary } = useStockSummary();
  const { data: movementsPage } = useMovements({ limit: 8 });

  const { data: forecastData, isLoading: loadingForecast, isRefetching: refetchingForecast, refetch: refetchForecast } = useQuery({
    queryKey: ['demandForecast'],
    queryFn: async () => {
      const { data } = await api.get('/ai/demand-forecast');
      return data as { predictions: Array<{ product: string; expected_qty: number; current_stock: number; unit: string; needs_restock: boolean; reason: string }> };
    },
    staleTime: 5 * 60 * 1000,
  });

  const items = itemsPage?.data ?? [];
  const movements = (movementsPage as any)?.items ?? [];

  // KPI derivations
  const totalItems = itemsPage?.total ?? 0;
  const outOfStock = items.filter((i: any) => (i.current_stock ?? 0) <= 0).length;
  const lowStock = items.filter((i: any) => {
    const stock = i.current_stock ?? 0;
    return i.min_stock && stock > 0 && stock <= i.min_stock;
  }).length;
  
  const estimatedValue = items.reduce((acc: number, item: any) => {
    const stock = item.current_stock ?? 0;
    return acc + (stock > 0 ? stock * (item.cost ?? 0) : 0);
  }, 0);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['inventory'] }),
      qc.invalidateQueries({ queryKey: ['demandForecast'] })
    ]);
    setTimeout(() => setRefreshing(false), 800);
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-8">

        {/* Header */}
        <PageHeader
          title="Inventario"
          subtitle="Control de insumos para heladería y comidas rápidas"
          actions={
            <>
              <button
                onClick={handleRefresh}
                className="inline-flex h-10 items-center gap-2 rounded-full border border-divider bg-surface px-4 text-sm font-semibold transition hover:bg-surface-muted"
              >
                <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
                Actualizar
              </button>
              <Link
                to="/inventory/items"
                className="inline-flex h-10 items-center gap-2 rounded-full bg-primary px-4 text-sm font-bold text-white shadow hover:opacity-90"
              >
                <Plus size={15} /> Nuevo ítem
              </Link>
            </>
          }
        />

        {/* Banner de Inventario Inicial (Si más del 50% de items están en 0 o negativo) */}
        {!loadingItems && totalItems > 0 && (outOfStock / totalItems) > 0.5 && (
          <div className="rounded-2xl border-2 border-primary bg-primary/5 p-6 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-4">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary">
                <ClipboardCheck size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-primary">Tu inventario no está cargado</h3>
                <p className="text-sm text-[color:var(--color-text)] mt-1 opacity-80 max-w-xl">
                  Hemos detectado que la mayoría de tus insumos ({outOfStock} de {totalItems}) están agotados o en negativo. Para que el sistema funcione correctamente, necesitas registrar las cantidades físicas que tienes en el local.
                </p>
              </div>
            </div>
            <Link
              to="/inventory/audit"
              className="shrink-0 inline-flex h-11 items-center gap-2 rounded-xl bg-primary px-6 text-sm font-bold text-white shadow-md hover:opacity-90 transition"
            >
              Hacer primer conteo
            </Link>
          </div>
        )}

        {/* Quick nav */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { to: '/inventory/items', icon: <Package size={20} />, label: 'Ítems & Categorías', desc: 'Crear y editar insumos' },
            { to: '/inventory/movements', icon: <TrendingDown size={20} />, label: 'Movimientos', desc: 'Entradas, salidas, ajustes' },
            { to: '/inventory/stock', icon: <BarChart3 size={20} />, label: 'Stock on-hand', desc: 'Stock en tiempo real' },
            { to: '/inventory/recipes', icon: <BookOpen size={20} />, label: 'Recetas', desc: 'Ingredientes por producto' },
          ].map(({ to, icon, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="group flex flex-col gap-2 rounded-2xl border border-divider bg-surface p-4 shadow-sm transition hover:border-primary hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-white">
                {icon}
              </div>
              <div>
                <p className="text-sm font-bold text-[color:var(--color-text)]">{label}</p>
                <p className="text-xs text-muted">{desc}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            icon={<Package size={72} />}
            label="Ítems totales"
            value={loadingItems ? '…' : totalItems}
            sub="productos registrados"
            color="blue"
          />
          <StatCard
            icon={<AlertTriangle size={72} />}
            label="Agotados"
            value={outOfStock}
            sub="requieren reposición urgente"
            color="red"
          />
          <StatCard
            icon={<TrendingDown size={72} />}
            label="Stock bajo"
            value={lowStock}
            sub="por debajo del mínimo"
            color="amber"
          />
          <StatCard
            icon={<BarChart3 size={72} />}
            label="Valor Inventario"
            value={`$${fmtNum(estimatedValue)}`}
            sub="capital inmovilizado"
            color="violet"
          />
        </div>

        {/* Alert: critical items */}
        {(outOfStock > 0 || lowStock > 0) && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-red-600" />
              <h2 className="font-bold text-red-800">Alerta de stock</h2>
            </div>
            <div className="space-y-2">
              {items
                .filter((i: any) => {
                  const stock = i.current_stock ?? 0;
                  return stock <= (i.min_stock ?? 0);
                })
                .slice(0, 5)
                .map((item: any) => {
                  const stock = item.current_stock ?? 0;
                  return (
                    <div key={item.id} className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-red-900">{item.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-700">
                          {fmtNum(stock)} {item.unit} disponibles
                        </span>
                        <StockBadge qty={stock} minStock={item.min_stock} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Widget: Predicción de Demanda IA */}
        <div className="rounded-2xl border border-divider bg-surface p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Sparkles size={20} className="text-primary animate-pulse" />
              <h2 className="text-lg font-bold text-[color:var(--color-text)]">Predicción de Demanda Inteligente (Proyección 3 días)</h2>
            </div>
            <button
              onClick={() => refetchForecast()}
              disabled={loadingForecast || refetchingForecast}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-divider px-3 text-xs font-semibold hover:bg-surface-muted transition disabled:opacity-50"
            >
              <RefreshCw size={12} className={loadingForecast || refetchingForecast ? 'animate-spin' : ''} />
              Refrescar
            </button>
          </div>

          {loadingForecast || refetchingForecast ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3 text-sm text-muted">
              <Loader2 className="animate-spin text-primary" size={28} />
              <span>Generando proyecciones con IA en base al historial...</span>
            </div>
          ) : !forecastData || !forecastData.predictions || forecastData.predictions.length === 0 ? (
            <p className="text-sm text-muted text-center py-6">No hay proyecciones de demanda disponibles en este momento.</p>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-xl border border-divider">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-surface-muted text-muted font-semibold border-b border-divider">
                      <th className="p-3">Insumo / Producto</th>
                      <th className="p-3 text-center">Stock Actual</th>
                      <th className="p-3 text-center">Demanda Proyectada</th>
                      <th className="p-3 text-center">Estado</th>
                      <th className="p-3">Recomendación IA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecastData.predictions.map((p, idx) => (
                      <tr key={idx} className="border-b border-divider/60 hover:bg-surface-muted/50 last:border-b-0">
                        <td className="p-3 font-semibold text-[color:var(--color-text)]">{p.product}</td>
                        <td className="p-3 text-center text-muted">
                          {p.current_stock ?? 0} {p.unit}
                        </td>
                        <td className="p-3 text-center text-primary font-bold">
                          {p.expected_qty ?? 0} {p.unit}
                        </td>
                        <td className="p-3 text-center">
                          {p.needs_restock ? (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
                              Reabastecer
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">
                              Óptimo
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-xs text-muted max-w-xs">{p.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {forecastData.predictions.some(p => p.needs_restock) && (
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      const criticalItems = forecastData.predictions
                        .filter(p => p.needs_restock)
                        .map(p => {
                          const item = items.find(i => i.name.toLowerCase().includes(p.product.toLowerCase()) || p.product.toLowerCase().includes(i.name.toLowerCase()));
                          return {
                            inventory_item_id: item ? item.id : '',
                            quantity: Math.max(1, Math.ceil(p.expected_qty - p.current_stock)),
                            unit: p.unit || 'kg',
                            unit_price: item?.cost ?? 0,
                          };
                        });
                      navigate('/purchases/new', { state: { prefillItems: criticalItems } });
                    }}
                    className="inline-flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-bold text-white shadow hover:opacity-90 transition"
                  >
                    <Plus size={15} /> Crear orden de compra pre-llenada
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Stock summary table */}
          <div className="rounded-2xl border border-divider bg-surface p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[color:var(--color-text)]">Estado del stock</h2>
              <Link to="/inventory/stock/summary" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
                Ver todo <ChevronRight size={13} />
              </Link>
            </div>
            {loadingItems ? (
              <div className="space-y-3">{[...Array(5)].map((_, i) => (
                <div key={i} className="h-8 animate-pulse rounded-xl bg-surface-muted" />
              ))}</div>
            ) : (
              <div className="space-y-1">
                {items.slice(0, 10).map((item: any) => {
                  const stock = item.current_stock ?? 0;
                  const pct = item.min_stock ? Math.min(100, (stock / (item.min_stock * 3)) * 100) : (stock <= 0 ? 0 : 50);
                  const barColor = stock <= 0 ? 'bg-red-500' : stock <= (item.min_stock ?? 0) ? 'bg-amber-400' : 'bg-emerald-500';
                  return (
                    <div key={item.id} className="group flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-surface-muted">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-semibold text-[color:var(--color-text)] truncate">{item.name}</span>
                          <span className="text-xs text-muted shrink-0">{fmtNum(stock)} {item.unit}</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-surface-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                            style={{ width: `${Math.max(3, pct)}%` }}
                          />
                        </div>
                      </div>
                      <StockBadge qty={stock} minStock={item.min_stock} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent movements */}
          <div className="rounded-2xl border border-divider bg-surface p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[color:var(--color-text)]">Movimientos recientes</h2>
              <Link to="/inventory/movements" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
                Ver todo <ChevronRight size={13} />
              </Link>
            </div>
            {movements.length === 0 ? (
              <p className="text-sm text-muted text-center py-8">No hay movimientos registrados aún.</p>
            ) : (
              <div>
                {movements.map((mov: any) => (
                  <RecentMovementRow key={mov._id ?? mov.id} mov={mov} />
                ))}
              </div>
            )}
          </div>
        </div>


      </div>
    </div>
  );
};

export default InventoryDashboard;
